import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Film, Newspaper, School,
  Users, Shield, Settings, ChevronLeft, ChevronRight,
  LogOut, Calendar, MapPin, Hash, Layers, ShoppingCart, Tag, Printer,
  BarChart2, UserCircle, GraduationCap
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const NAV = [
  { section: 'Main', items: [
    { to: '/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard', perm: null },
  ]},
  { section: 'Content', items: [
    { to: '/tutorials',      icon: <BookOpen size={18} />,  label: 'Tutorials',      perm: ['tutorials',      'view'] },
    { to: '/creator-videos', icon: <Film size={18} />,      label: 'Creator Videos', perm: ['creator_videos', 'view'] },
    { to: '/blog-videos',    icon: <Newspaper size={18} />, label: 'Blog Videos',    perm: ['blog_videos',    'view'] },
  ]},
  { section: 'Operations', items: [
    { to: '/batches',      icon: <Layers size={18} />,       label: 'Batches',      perm: ['batches',       'view'] },
    { to: '/schools',      icon: <School size={18} />,       label: 'Schools',      perm: ['schools',       'view'] },
    { to: '/orders',       icon: <ShoppingCart size={18} />, label: 'Orders',       perm: ['orders',        'view'] },
    { to: '/direct-codes', icon: <Tag size={18} />,          label: 'Direct Codes', perm: ['direct_codes',  'view'] },
  ]},
  { section: 'Reports', items: [
    { to: '/redemption-report', icon: <BarChart2 size={18} />, label: 'Code Redemption', perm: ['reports', 'view'] },
    { to: '/end-users', icon: <UserCircle size={18} />, label: 'End Users', perm: ['reports', 'view'] }
// ← added inside Operations section, after Direct Codes
  ]},
  { section: 'Admin', items: [
    { to: '/users', icon: <Users size={18} />,  label: 'Users', perm: ['users', 'view'] },
    { to: '/school-users', icon: <GraduationCap size={18} />,  label: 'School Users',  perm: ['users', 'view'] },
    { to: '/roles', icon: <Shield size={18} />, label: 'Roles', perm: ['roles', 'view'] },
    { label: 'Masters', icon: <Settings size={18} />, perm: ['masters', 'view'],
      children: [
        { to: '/masters/academic-years', icon: <Calendar size={16} />, label: 'Academic Years' },
        { to: '/masters/districts',      icon: <MapPin size={16} />,   label: 'Districts' },
        { to: '/masters/pincodes',       icon: <Hash size={16} />,     label: 'Pincodes' },
      ]
    },
  ]},
];

export default function Sidebar({ collapsed, onToggle }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [mastersOpen, setMastersOpen] = useState(false);

  const handleLogout = async () => { await logout(); toast.success('Logged out'); navigate('/login'); };
  const initials = user?.fullName?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'SA';

  // perm=null → always visible; perm=['module','action'] → check can()
  // SCHOOL role always sees the redemption report (API scopes data to their school)
  const canSee = (perm) => !perm || can(perm[0], perm[1]) || user?.role === 'SCHOOL';

  return (
    <nav className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">S</div>
        {!collapsed && <div className="sidebar-logo-text">Swardhara<span>Admin Portal</span></div>}
      </div>

      <div className="sidebar-nav">
        {NAV.map(section => (
          <div key={section.section}>
            {!collapsed && <div className="sidebar-section-label">{section.section}</div>}
            {section.items.map(item => {
              if (!canSee(item.perm)) return null;

              // Masters accordion
              if (item.children) return (
                <div key={item.label}>
                  <div
                    className={`nav-item ${mastersOpen ? 'active' : ''}`}
                    onClick={() => setMastersOpen(v => !v)}>
                    {item.icon}
                    {!collapsed && <>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      <span style={{ fontSize: 12 }}>{mastersOpen ? '▴' : '▾'}</span>
                    </>}
                  </div>
                  {mastersOpen && !collapsed && (
                    <div style={{ paddingLeft: 16 }}>
                      {item.children.map(c => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                          style={{ fontSize: 13 }}>
                          {c.icon}<span>{c.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );

              // Regular nav item
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
                  {item.icon}
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>

      {/* User footer */}
      <div style={{ padding: collapsed ? '12px 0' : '12px 16px', borderTop: '1px solid var(--orange-100)', display: 'flex', alignItems: 'center', gap: 10, justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <div className="user-avatar" style={{ width: 32, height: 32, flexShrink: 0 }}>{initials}</div>
        {!collapsed && <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--grey-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.fullName}</div>
            <div style={{ fontSize: 10, color: 'var(--grey-400)' }}>{user?.roleDisplayName}</div>
          </div>
          <button className="btn-icon" onClick={handleLogout} title="Logout"><LogOut size={15} /></button>
        </>}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        style={{ position: 'absolute', top: '50%', right: -12, width: 24, height: 24, background: 'var(--white)', border: '1.5px solid var(--orange-200)', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--orange-400)', boxShadow: 'var(--shadow-sm)' }}>
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </nav>
  );
}
